import isNil from 'lodash/isNil'
import isObjectLike from 'lodash/isObjectLike'
import { nanoid } from 'nanoid'
import qs from 'qs'

import type { GETGenericListResponse, OffsetSearchParams } from '@/api/types'
import { MFA_TOKEN_REGEXP } from '@/constants/email'
import type { OptionType } from '@/constants/types'

import type {
  DynamicOptionType,
  ForceRefetchProps,
  FormatPriceString,
  FormatPriceStringFromCents,
  GetEmailModalDefaultValues,
  Pluralize,
} from './types'

export const generateId = (length: number): string => nanoid(length)
export const preventWheelInput = (e) => e.target.blur()

export const pluralize: Pluralize = (val, word, plural = `${word}s`) => {
  const _pluralize = (num, word, plural = `${word}s`) =>
    [1, -1].includes(Number(num)) ? word : plural
  if (isObjectLike(val)) {
    return (num, word) => _pluralize(num, word, val[word])
  }
  return _pluralize(val, word, plural)
}

export const formatPriceString: FormatPriceString = (value, fractionDigits) =>
  (!isNil(value) &&
    value.toLocaleString('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })) ||
  '0'

export const formatPriceStringFromCents: FormatPriceStringFromCents = (
  priceInCents,
  fractionDigits = 2
) => {
  if (!priceInCents) return '0'
  return formatPriceString(priceInCents / 100, fractionDigits)
}

export const formatPrice = (
  value: number,
  currencySymbol?: string,
  isValueInCents: boolean = true
) =>
  `${currencySymbol || ''}${
    isValueInCents
      ? formatPriceStringFromCents(value)
      : formatPriceString(value, 2)
  }`

export const convertToMin = (value: number) => `${Math.round(value / 60)} Min`

export const SHORT_HEX_REG_EXP = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
export const LONG_HEX_REG_EXP = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i

export const convertHexInRGBA = (hexColor: string, alpha = 1) => {
  const color = hexToRgb(hexColor)

  if (isNil(color)) {
    return null
  }

  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`
}

export const hexToRgb = (rowHex) => {
  if (typeof rowHex !== 'string') {
    return null
  }

  const hex = rowHex.replace(
    SHORT_HEX_REG_EXP,
    (m, r, g, b) => r + r + g + g + b + b
  )
  const result = LONG_HEX_REG_EXP.exec(hex)

  if (isNil(result)) {
    return null
  }

  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  }
}

export const rgba = (hex, alpha) => {
  const color = hexToRgb(hex)
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`
}

export const getPublicDomain = (companySlug: string) =>
  process.env.BASE_PUBLIC_DOMAIN.replace('{company}', companySlug)

const getMessageFromRmc = ({
  name,
  alternative,
}: {
  name: string
  alternative: boolean
}) =>
  alternative
    ? `Dear ${name || 'Guest'}`
    : `Hello ${name || ''},\nHere are available properties for your request:`

const getEmailSubject = ({
  reference,
  lastName,
  emailTemplate,
}: {
  reference: string
  lastName?: string
  emailTemplate?: string
}) => {
  const _reference = reference || ''
  const _lastName = lastName || ''
  if (emailTemplate === 'HPE') {
    return `Temporary Accommodation Options ${_reference} \u002F HPE \u002F ${_lastName}`
  }
  return `Housing Options for Request ${_reference}`
}

export const getEmailModalDefaultValues: GetEmailModalDefaultValues = ({
  hor,
  personInfoKey = '',
}) => {
  const showAlternativeEmailPlaceholder =
    hor?.company?.horEmailTemplate !== 'Standard'

  return {
    subject: getEmailSubject({ reference: hor?.reference }),
    employeeName: hor?.[personInfoKey]?.firstName,
    employeeEmail: hor?.[personInfoKey]?.email,
    hidePrice: !hor?.hidePrice,
    messageFromRmc: getMessageFromRmc({
      name: hor?.[personInfoKey]?.firstName,
      alternative: showAlternativeEmailPlaceholder,
    }),
  }
}

export const extractMFA = (
  emailBody: string
): {
  isMFAEmail: boolean
  token: string | null
} => {
  const mfaMatch = emailBody.match(MFA_TOKEN_REGEXP)
  if (mfaMatch && mfaMatch[1]) {
    return { isMFAEmail: true, token: mfaMatch[1] }
  }
  return { isMFAEmail: false, token: null }
}

export const isAccommodationEmail = (emailBody: string): boolean =>
  emailBody.includes('accommodation') || emailBody.includes('accomodation')

export const stringifyParams = (params: Record<string, any>) =>
  params
    ? `${Object.keys(params).length ? '?' : ''}${qs.stringify(params || {})}`
    : ''

export const convertUTCToLocalDate = (date) => {
  if (!date) {
    return date
  }
  let newDate = new Date(date)
  newDate = new Date(
    newDate.getUTCFullYear(),
    newDate.getUTCMonth(),
    newDate.getUTCDate()
  )
  return newDate
}

export const convertLocalToUTCDate = (date) => {
  if (!date) {
    return date
  }
  let newDate = new Date(date)
  newDate = new Date(
    Date.UTC(newDate.getFullYear(), newDate.getMonth(), newDate.getDate())
  )
  return newDate
}

export const isValidDate = (dateObject) =>
  dateObject && new Date(dateObject).toString() !== 'Invalid Date'

export const valueLabelOption = <V, L>(
  value: V,
  label: L
): DynamicOptionType<V, L> => ({
  value,
  label,
})

type OmitT<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>

export const omit = <T, K extends keyof T>(obj: T, keys: K[]): OmitT<T, K> =>
  Object.keys(obj).reduce(
    (acc, key) => {
      if (obj.hasOwnProperty(key) && !keys.includes(key as K)) {
        acc[key] = obj[key]
      }
      return acc
    },
    {} as Omit<T, K>
  )

export const valueToNumber = (value: number | string): number =>
  value ? Number(value) : null

export const optionTypeGuard = (v: any): v is OptionType =>
  typeof v === 'object' &&
  typeof v?.label === 'string' &&
  (typeof v?.value === 'string' || typeof v?.value === 'number')

export const getCacheKey = (
  endpointName: string,
  queryArgs: OffsetSearchParams
) => {
  const cacheKeys = [endpointName]

  if (queryArgs.mergable) {
    cacheKeys.push('mergable')
  } else {
    cacheKeys.push('default')
  }

  if (queryArgs.text) {
    cacheKeys.push(`text=${queryArgs.text}`)
  }

  if (queryArgs.ids?.length) {
    const sortedIds = queryArgs.ids.slice().sort()
    cacheKeys.push(`ids=${sortedIds.join(',')}`)
  }

  if (queryArgs.slugList?.length) {
    const sortedSlugs = queryArgs.slugList.slice().sort()
    cacheKeys.push(`slugList=${sortedSlugs.join(',')}`)
  }

  if (queryArgs.rmcRole) {
    cacheKeys.push(`rmcRole=${queryArgs.rmcRole}`)
  }

  return cacheKeys.join(':')
}

export const getMergableConfig = <
  Response extends GETGenericListResponse<any>,
  Args extends OffsetSearchParams,
>() => ({
  serializeQueryArgs: ({
    endpointName,
    queryArgs,
  }: {
    endpointName: string
    queryArgs: Args
  }) => getCacheKey(endpointName, queryArgs),
  merge: (
    currentCache: Response,
    newItems: Response & { args: { offset?: number; mergable?: boolean } }
  ) => {
    const { mergable, offset } = newItems.args

    // If the new items are not mergable, or if the offset is less than the current cache length

    if (!mergable) {
      // eslint-disable-next-line no-param-reassign
      currentCache.items = newItems.items
    } else if (offset < currentCache.items.length) {
      const endIndex = Math.min(
        offset + newItems.items.length,
        currentCache.items.length
      )

      // In currentCache.items I can have more items than in newItems.items
      // If I have 20 items in currentCache.items and in newItems I have 10 items with these args: {offset: 0, limit 10}
      // I will replace the first 10 items from currentCache.items with the items in newItems.items.
      // Then if I have in newItems another 10 items with args: {offset: 10, limit 10}
      // I would need to replace the second 10 items of currentCache.items
      currentCache.items.splice(offset, endIndex - offset, ...newItems.items)
    } else {
      currentCache.items.push(...newItems.items)
    }
  },
  forceRefetch({ currentArg, previousArg }: ForceRefetchProps<Args>) {
    if (!previousArg) return true
    if (currentArg.text !== previousArg.text) return false
    return currentArg.offset !== previousArg.offset
  },
  transformResponse: (value, _meta, args) => ({
    ...value,
    args,
  }),
})
